import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Card from 'reactstrap/lib/Card';

import {
  BuildPriceCreativeConfigDefaultProps,
  BuildPriceCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  CommonAdDefaultProps,
  CommonAdPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';

import { BUILD_PRICE_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { definePlatform, getAdImageUrl } from 'site-modules/shared/components/native-ad/utils/utils';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { CtaUI } from 'client/site-modules/shared/components/native-ad/multi-cta-ad/components/cta';

import './buildprice-site-served-ad.scss';

export function BuildPriceSiteServedAdCtaOnlyUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  customTargeting = CommonAdDefaultProps.customTargeting,
  vehicle = SiteServedAdDefaultProps.vehicle,
  currentVehicle = SiteServedAdDefaultProps.currentVehicle,
  creativeConfig = BuildPriceCreativeConfigDefaultProps,
  mobile = SiteServedAdDefaultProps.mobile,
  position = SiteServedAdDefaultProps.position,
  wrapperClass = null,
  isConquest = SiteServedAdDefaultProps.isConquest,
  onAdLoadedCallback,
}) {
  const {
    siteServedAdsUpdater,
    creativeConfigData,
    traffickingData: { clickTracker },
  } = creativeConfig;

  const {
    cta,
    lineItemId,
    siteServedCreativeId,
    target,
    vehicleInfo,
    photo,
    photoDisclaimer,
    msrp: msrpConfig,
    trimNames,
    logo,
    linkDisplayUrl,
  } = creativeConfigData;

  const loadTrackingListener = useCallback(
    event => {
      adLoadTrackingListener(event, cta);
    },
    [adLoadTrackingListener, cta]
  );

  // Compute derived values together
  const vehicleImg = getAdImageUrl(photo);

  // Memoize the callback data object to prevent unnecessary re-renders
  const adLoadedData = useMemo(
    () => ({
      jellybeanImage: {
        src: vehicleImg,
        photoDisclaimer,
      },
      features: {
        vehicleInfo,
        msrpConfig,
        trimNames,
      },
      logo,
      isConquest,
      linkDisplayUrl,
    }),
    [vehicleImg, photoDisclaimer, vehicleInfo, msrpConfig, trimNames, logo, isConquest, linkDisplayUrl]
  );

  useEffect(() => {
    onAdLoadedCallback(adLoadedData);
  }, [onAdLoadedCallback, adLoadedData]);

  if (!cta) {
    return null;
  }

  const targetVehicle = currentVehicle || vehicle;
  const ctaURL = clickTracker[definePlatform(mobile)];
  const ctaTarget = target || '_blank';
  const modelYear = get(vehicleInfo, 'year', '');

  const nativeStyle = BUILD_PRICE_AD.NATIVE_STYLE;

  return (
    <Card
      className={classnames('buildprice-site-served pos-r', wrapperClass)}
      data-tracking-parent={nativeStyle}
      data-viewability={`${nativeStyle}-${position}`}
      data-make-name={get(targetVehicle, 'make.name', '')}
      data-model-name={get(targetVehicle, 'model.name', '')}
      data-model-year={modelYear}
      innerRef={adRef}
      style={{ overflow: 'hidden', width: 'auto', border: 'none' }}
    >
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={nativeStyle}
        position={position}
        currentVehicle={currentVehicle}
        customTargeting={customTargeting}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <CtaUI
        ctaURL={ctaURL}
        ctaTarget={ctaTarget}
        adClickTrackingListener={adClickTrackingListener}
        cta={'Build & Price'}
      />
    </Card>
  );
}

BuildPriceSiteServedAdCtaOnlyUI.propTypes = {
  ...SiteServedAdPropTypes,
  customTargeting: CommonAdPropTypes.customTargeting,
  creativeConfig: BuildPriceCreativeConfigPropTypes,
  wrapperClass: PropTypes.string,
  onAdLoadedCallback: PropTypes.func.isRequired,
};

export const BuildPriceSiteServedAdCtaOnly = siteServedAdTrackingWrapper(BuildPriceSiteServedAdCtaOnlyUI);
